import { MINUTE_MS } from '@calm/web-common/dist/time';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useGetPartnerById } from '@rhg/hooks/use-partner';
import { useTracking } from '@rhg/libs/analytics';
import createCircleApi from '@rhg/libs/api/create-circle-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { queryKeys, useQuery } from '@rhg/libs/react-query';
import { useMostRecentSponsorship } from '../use-most-recent-sponsorship';

export const RESOURCE_LIMIT_PER_THEME = 1;

function getQueryKey(
    themes: string[] | undefined,
    sponsorshipId: string | undefined,
    hasLostSponsorship: boolean,
    shouldLimitResourcesPerTheme: boolean | undefined
) {
    const queryKey = [
        queryKeys.resources,
        sponsorshipId,
        hasLostSponsorship,
        shouldLimitResourcesPerTheme,
    ];
    if (themes && themes.length > 0) {
        queryKey.push(...themes.slice().sort());
    }
    return queryKey;
}

export function useResources({
    themes,
    shouldLimitResourcesPerTheme,
}: {
    themes?: string[];
    shouldLimitResourcesPerTheme?: boolean;
} = {}) {
    const { setUserProfile } = useTracking();
    const { isAuthenticated, getAccessTokenSilently, isLoading } = useAuth();
    const { activeCircle } = useActiveCircle();
    const {
        mostRecentSponsorship,
        hasLostSponsorship,
        isFetched: isMostRecentSponsorshipFetched,
    } = useMostRecentSponsorship();
    const mostRecentSponsorshipId =
        mostRecentSponsorship?.recentSponsor?.sponsorshipConfigId;
    const { getPartnerByIdAsync } = useGetPartnerById();
    const partnerId = mostRecentSponsorship?.recentSponsor?.partnerId;

    const { data, ...otherProps } = useQuery({
        queryKey: getQueryKey(
            themes,
            mostRecentSponsorshipId,
            hasLostSponsorship,
            shouldLimitResourcesPerTheme
        ),
        queryFn: async () => {
            if (!mostRecentSponsorshipId || hasLostSponsorship) {
                return [];
            }

            /* Set an analytics prop to track if resources are enabled for the user */
            if (partnerId) {
                const partner = await getPartnerByIdAsync(partnerId);
                if (partner) {
                    await setUserProfile({
                        HasResourcesEnabled: partner.healthResourcesPublished,
                        HasSmartResourcesEnabled: partner.smartResourcesEnabled,
                    });
                }
            }

            const api = createCircleApi(getAccessTokenSilently);
            const response = await api.getResources({
                circleAuthorizationToken: activeCircle!.authorizationJWT,
                recipientId: activeCircle!.recipient.id,
                themes,
                limitPerTheme: shouldLimitResourcesPerTheme
                    ? RESOURCE_LIMIT_PER_THEME
                    : undefined,
            });
            return response.data.partnerHealthResources;
        },
        enabled:
            !isLoading &&
            isAuthenticated &&
            !!activeCircle?.recipient.id &&
            !!activeCircle?.authorizationJWT &&
            isMostRecentSponsorshipFetched,
        staleTime: 5 * MINUTE_MS,
    });

    return {
        resources: data,
        ...otherProps,
    };
}
